import React from 'react'
import { RequestContext } from '../Contexts/RequestContext'
import {
  DarwinHelpers,
  getDocumentsInitialState,
  getDocumentsReducer,
  requestAnomaliesInitialState,
  requestAnomaliesReducer,
  requestContactsCountInitialState,
  requestContactsCountReducer,
  requestContactsInitialState,
  requestContactsReducer,
  requestDomainsCountInitialState,
  requestDomainsCountReducer,
  requestDomainsInitialState,
  requestDomainsReducer,
  requestEventsInitialState,
  requestEventsReducer,
  requestsByIdInitialState,
  requestsByIdReducer,
  requestsRequiredDocumentsInitialState,
  requestsRequiredDocumentsReducer,
} from '@nic/nic-api'
import { Darwin, NicScope } from '@nic/nic-api/types'
import { useAuth } from '@nic/kc-auth'
import { useRouter } from '../Hooks/useRouter'

export const RequestProvider: React.FC = ({ children }) => {
  const { match } = useRouter()
  const { keycloak } = useAuth()
  const idParam = Number((match.params as { id?: string }).id)
  const id = Number.isNaN(idParam) ? undefined : idParam
  const scope = keycloak?.realm as NicScope
  const [stateReqById, dispatchReqById] = React.useReducer(
    requestsByIdReducer,
    requestsByIdInitialState
  )
  const [stateReqAnomalies, dispatchReqAnom] = React.useReducer(
    requestAnomaliesReducer,
    requestAnomaliesInitialState
  )

  const [stateReqEvents, dispatchReqEvents] = React.useReducer(
    requestEventsReducer,
    requestEventsInitialState
  )

  const [stateReqContacts, dispatchReqContacts] = React.useReducer(
    requestContactsReducer,
    requestContactsInitialState
  )

  const [stateReqDomains, dispatchReqDomains] = React.useReducer(
    requestDomainsReducer,
    requestDomainsInitialState
  )

  const [stateReqDomainsCount, dispatchReqDomainsCount] = React.useReducer(
    requestDomainsCountReducer,
    requestDomainsCountInitialState
  )

  const [stateReqContactsCount, dispatchReqContactsCount] = React.useReducer(
    requestContactsCountReducer,
    requestContactsCountInitialState
  )

  const [stateRequiredDocuments, dispatchRequiredDocuments] = React.useReducer(
    requestsRequiredDocumentsReducer,
    requestsRequiredDocumentsInitialState
  )
  const [stateReqDocuments, dispatchReqDocuments] = React.useReducer(
    getDocumentsReducer,
    getDocumentsInitialState
  )
  // const [stateReqExpireDays, dispatchReqExpireDays] = React.useReducer(
  //   requestExpirationDayReducer,
  //   requestExpirationDaysByIdInitialState
  // )

  const requestLoad = React.useCallback(
    (_id: number) => {
      DarwinHelpers.request(dispatchReqById, _id, scope)
      DarwinHelpers.anomalies(dispatchReqAnom, _id, scope)
      DarwinHelpers.events(dispatchReqEvents, { requestId: _id, size: 5, page: 0 }, scope)
      DarwinHelpers.contacts(dispatchReqContacts, { requestId: _id, size: 5, page: 0 }, scope)
      DarwinHelpers.domains(dispatchReqDomains, { requestId: _id, size: 5, page: 0 }, scope)
      DarwinHelpers.domainsCount(dispatchReqDomainsCount, { requestId: _id }, scope)
      DarwinHelpers.contactsCount(dispatchReqContactsCount, { requestId: _id }, scope)
      DarwinHelpers.documents(dispatchReqDocuments, { requestId: _id }, scope)
    },
    [
      scope,
      dispatchReqById,
      dispatchReqAnom,
      dispatchReqEvents,
      dispatchReqContacts,
      dispatchReqDomains,
      dispatchReqDomainsCount,
      dispatchReqContactsCount,
      dispatchReqDocuments,
    ],
  )

  const reload = React.useCallback(() => {
    if (typeof id !== 'number') {
      return
    }
    requestLoad(id)
  }, [id, requestLoad])

  function cleanRequiredDocument() {
    dispatchRequiredDocuments({ type: 'POST_REQUEST_REQUIRED_DOCUMENTS_CLEAN' })
  }

  const reloadDocs = React.useCallback(() => {
    if (typeof id !== 'number') {
      return
    }
    DarwinHelpers.documents(dispatchReqDocuments, { requestId: id }, scope)
  }, [id, scope, dispatchReqDocuments])

  const loadDomains = React.useCallback(
    (page: number, size: number) => {
      if (typeof id !== 'number') {
        return
      }
      DarwinHelpers.domains(dispatchReqDomains, { requestId: id, size, page }, scope)
    },
    [id, scope, dispatchReqDomains],
  )

  const postRequiredDocuments = React.useCallback(
    (data: Darwin.RequiredDocumentsBody) => {
      if (typeof id !== 'number') {
        return
      }
      DarwinHelpers.postRequiredDocuments(dispatchRequiredDocuments, id, data)
    },
    [id, dispatchRequiredDocuments],
  )

  React.useEffect(() => {
    if (typeof id !== 'number') {
      return
    }
    reload()
  }, [id, reload])

  return (
    <RequestContext.Provider
      value={{
        stateReqById,
        stateReqAnomalies,
        stateReqEvents,
        stateReqContacts,
        stateReqDomains,
        stateReqContactsCount,
        stateReqDomainsCount,
        stateReqDocuments,
        stateRequiredDocuments,
        dispatchReqDocuments,
        dispatchReqEvents,
        scope,
        id,
        reload,
        load: requestLoad,
        loadDomains,
        loadDocuments: reloadDocs,
        postRequiredDocuments,
        cleanRequiredDocument: () => cleanRequiredDocument(),
      }}
    >
      {children}
    </RequestContext.Provider>
  )
}
